import {
  HARD_MOODS,
  MOODS,
  type Mood,
  addDays,
  todayIso,
  useIsOnline,
  useMoodHistory,
  useSetMood,
} from '@shkola/api';
import { type ToneName, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonClass } from '../../design/ui';
import { type StudentContext, weekdayShort } from './model';

/*
 * «Як ти сьогодні?» on the home (his «ставим», 27.10): the daily one-tap check of «Турбота» — the
 * psychologist sees a class's mood only when five or more answer, and they answer when asked here. Faces
 * are drawn (no emoji in v4); a hard day gets a gentle offer, never a lecture; the week below.
 */

export const MOOD_TONES: Record<Mood, ToneName> = {
  great: 'green',
  good: 'teal',
  okay: 'yellow',
  sad: 'blue',
  angry: 'red',
  anxious: 'violet',
};

/** A face on a pastel circle, its features in the tone's bright colour. */
export function MoodFace({ mood, size = 40 }: { mood: Mood; size?: number }) {
  const t = tones[MOOD_TONES[mood]];
  const ink = t.vivid;
  const line = { stroke: ink, strokeWidth: 2.4, strokeLinecap: 'round' as const, fill: 'none' };
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className="overflow-visible"
    >
      <circle cx="20" cy="20" r="19" fill={t.from} />
      <circle
        cx="20"
        cy="20"
        r="19"
        fill="none"
        stroke="#FFFFFF"
        strokeOpacity="0.7"
        strokeWidth="1.5"
      />
      {mood === 'great' ? (
        <>
          <path d="M11.5 17q2.5-3.5 5 0M23.5 17q2.5-3.5 5 0" {...line} />
          <path d="M12.5 23h15q-1.5 7-7.5 7t-7.5-7Z" fill={ink} />
        </>
      ) : (
        <>
          {mood === 'angry' && <path d="M11.5 13.5l5 2.5M28.5 13.5l-5 2.5" {...line} />}
          {mood === 'anxious' && <path d="M11.5 14.5l5-2M28.5 14.5l-5-2" {...line} />}
          <circle cx="14.5" cy="18" r="2" fill={ink} />
          <circle cx="25.5" cy="18" r="2" fill={ink} />
          {mood === 'good' && <path d="M14 25q6 5 12 0" {...line} />}
          {mood === 'okay' && <path d="M14.5 26h11" {...line} />}
          {mood === 'sad' && (
            <>
              <path d="M14 28q6-5 12 0" {...line} />
              <path d="M29 21.5q1.6 2.4 0 3.6q-1.6-1.2 0-3.6Z" fill={tones.blue.icon} />
            </>
          )}
          {mood === 'angry' && <path d="M14.5 27.5q5.5-3.5 11 0" {...line} />}
          {mood === 'anxious' && <path d="M13.5 26.5q1.8-2 3.6 0t3.6 0t3.6 0t3.6 0" {...line} />}
        </>
      )}
    </svg>
  );
}

export function GlassMood({
  ctx,
  className = '',
  frame,
}: {
  ctx: StudentContext;
  className?: string;
  /** the block's own frame instead of the white card of «Скло» */
  frame?: string;
}) {
  const { t } = useTranslation();
  const history = useMoodHistory(ctx.child.memberId, 7);
  const setMood = useSetMood();
  const online = useIsOnline();
  const today = todayIso();
  const [picked, setPicked] = useState<Mood>();
  const [changing, setChanging] = useState(false);
  const saved = history.data?.find((d) => d.day === today)?.mood;
  const mood = picked ?? saved;
  const byDay = new Map((history.data ?? []).map((d) => [d.day, d.mood]));
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));

  const choose = (m: Mood) => {
    setPicked(m);
    setChanging(false);
    setMood.mutate({ schoolId: ctx.schoolId, mood: m });
  };

  return (
    <section
      className={`flex min-w-0 flex-col ${frame ?? 'rounded-[32px] bg-white p-5 shadow-[0_18px_40px_-28px_rgb(30_42_94/0.35)] md:p-6'} ${className}`}
    >
      <header className="mb-4">
        <h2 className="font-display text-[18px] leading-6 font-semibold text-ink">
          {t('care.mood.question')}
        </h2>
        <p className="mt-0.5 text-[13px] text-muted">{t('studentHome.glass.mood.hint')}</p>
      </header>

      {mood && !changing ? (
        <div className="flex flex-col gap-3">
          <div
            className="flex items-center gap-3 rounded-[20px] p-3"
            style={{ backgroundColor: tones[MOOD_TONES[mood]].soft }}
          >
            <MoodFace mood={mood} size={48} />
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-ink">{t(`care.moods.${mood}`)}</p>
              <p className="text-[12.5px] leading-[18px] text-ink-soft">
                {!online
                  ? t('care.mood.queued')
                  : HARD_MOODS.has(mood)
                    ? t('care.mood.hard')
                    : t('care.mood.good')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="shrink-0 self-start text-[12.5px] font-semibold text-ink-soft hover:text-ink"
            >
              {t('studentHome.glass.mood.change')}
            </button>
          </div>
          {HARD_MOODS.has(mood) && (
            <div className="flex flex-wrap gap-2">
              <Link to="/care" search={{ open: 'calm' }} className={buttonClass('outline', 'sm')}>
                {t('care.actions.calm')}
              </Link>
              <Link to="/care" className={buttonClass('primary', 'sm')}>
                {t('care.actions.write')}
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1.5">
          {MOODS.map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mood === m}
              onClick={() => choose(m)}
              className="flex flex-col items-center gap-1 rounded-[18px] px-1 py-2 transition-[background-color,transform] duration-300 ease-spring hover:-translate-y-0.5"
              style={{ backgroundColor: mood === m ? tones[MOOD_TONES[m]].soft : '#F5F6F9' }}
            >
              <MoodFace mood={m} size={38} />
              <span className="text-[12px] font-semibold text-ink-soft">
                {t(`care.moods.${m}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-auto pt-4">
        <p className="mb-2 text-[12px] font-semibold text-faint">
          {t('studentHome.glass.mood.week')}
        </p>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const m = byDay.get(d) ?? (d === today ? mood : undefined);
            return (
              <span key={d} className="flex flex-col items-center gap-1">
                {m ? (
                  <MoodFace mood={m} size={26} />
                ) : (
                  <span className="size-[26px] rounded-full bg-[#F0F1F5]" />
                )}
                <span
                  className={`text-[11px] ${d === today ? 'font-semibold text-ink' : 'text-faint'}`}
                >
                  {weekdayShort(d)}
                </span>
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}
